
import React, { useEffect, useRef, useMemo } from 'react';
import { Check, ArrowLeft } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import '../Style/DeviceFotaDeploymentStatus.css';

const API_BASE_URL = "https://api.yellowmatrix.co.in";

const fetchWithAuth = async (url) => {
  // NOTE: This is a hardcoded token for demonstration. In production, use localStorage.getItem('token')
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_BASE_URL}${url}`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!response.ok) throw new Error("Failed to fetch status");
  return await response.json();
};

// --- Helper for Dynamic Colors based on Label ---
const getStepColor = (label, status) => {
  if (status === 'completed') {
    switch (label) {
      case "Request Pending": return "gray-500";
      case "Request Received": return "amber-800";
      case "Package Downloading": return "sky-400";
      case "Update Staging": return "purple-700";
      case "Waiting For Preconditions": return "indigo-900";
      case "Update in Progress": return "blue-500"; 
      case "Update Complete": return "green-500";

      default: return "slave-500"; // Fallback for other completed steps
    }
  }
  if (status === 'inProgress' || status === 'inprogress') return "blue-500";
  if (status === 'failed') return "red-500";
  return "gray-300"; // Default for pending
};


// --- Sub-component: Step ---
const Step = ({ step }) => {
  const { label, status } = step;
  // Get color based on label and status
  const color = getStepColor(label, status);

  const renderIndicator = () => {
    if (status === "completed") {
      return (
        // Use dynamic-bg- class for specific colors
        <div className={`progress-step-indicator-FotaStatus completed-FotaStatus dynamic-bg-${color}-FotaStatus`}>
          <Check className="icon-size-default-FotaStatus" />
        </div>
      );
    } else if (status === "inprogress" || status === "inProgress") {
       // Simple spinner for active state
      return (
        <div className="progress-step-circle-wrapper-FotaStatus">
          <div className="spinner-loader-FotaStatus"></div>
        </div>
      );
    } else {
      return <div className={`progress-step-indicator-FotaStatus pending-FotaStatus`}></div>;
    }
  };

  return (
    <div className={`progress-step-item-FotaStatus`}>
      {renderIndicator()}
      {/* Use dynamic-color- class for text color */}
      <div className={`progress-step-label-FotaStatus ${status === 'completed' ? `dynamic-color-${color}-FotaStatus` : ''} ${status === 'inprogress' || status === 'inProgress' ? 'current-status-FotaStatus' : ''}`}>
        {label}
      </div>
    </div>
  );
};

// --- Sub-component: Progress Bar ---
const ProgressBar = ({ steps }) => {
  return (
    <div className="progress-tracker-container-FotaStatus">
      {steps.map((step, index) => {
        // Get color for the connecting line based on the *current* step's label and status
        const lineColor = getStepColor(step.label, step.status);
        return (
        <React.Fragment key={index}>
          <Step step={step} />
          {index < steps.length - 1 && (
            // Apply dynamic background color to the line if the step is completed
            <div className={`progress-connecting-line-FotaStatus ${step.status === 'completed' ? `dynamic-bg-${lineColor}-FotaStatus` : ''}`}></div>
          )}
        </React.Fragment>
        );
      })}
    </div>
  );
};

// --- Sub-component: Phases Chart ---
const PhasesCompleted = ({ pieData, barData, totalDevices }) => {
  const canvasRef = useRef(null);

  // Define colors for Pie Chart
  const pieColors = {
    "Pending": "#A0AEC0",
    "In Progress": "#4299E1",
    "Aborted": "#FC8181",
    "Failed": "#F56565",
    "Complete": "#48BB78"
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !pieData) return;

    const ctx = canvas.getContext("2d");
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = Math.min(centerX, centerY) * 0.9;

    let total = Object.values(pieData).reduce((a, b) => a + b, 0);
    // Avoid division by zero
    if(total === 0) total = 1;

    let startAngle = -Math.PI / 2;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    Object.entries(pieData).forEach(([key, value]) => {
      if (value === 0) return;
      const sliceAngle = (value / total) * 2 * Math.PI;
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, startAngle, startAngle + sliceAngle);
      ctx.closePath();
      ctx.fillStyle = pieColors[key] || "#ccc";
      ctx.fill();
      startAngle += sliceAngle;
    });
  },);

  return (
    <div className="phases-card-layout-FotaStatus">
      <div className="phases-chart-column-FotaStatus">
        <canvas ref={canvasRef} width="180" height="180"></canvas>
        <div className="phases-legend-FotaStatus">
          {Object.entries(pieData).map(([key, val], idx) => (
            <div key={idx} className="legend-row-FotaStatus">
               <div className="legend-label-group-FotaStatus">
                <span className="legend-dot-FotaStatus" style={{ backgroundColor: pieColors[key] }}></span>
                <span className="legend-name-FotaStatus">{key}</span>
              </div>
              <span className="legend-val-FotaStatus">{val}</span>
            </div>
          ))}
          <div className="targeted-total-FotaStatus">
            <span>Total Devices</span>
            <strong>{totalDevices}</strong>
          </div>
        </div>
      </div>

      <div className="phases-bars-column-FotaStatus">
        {Object.entries(barData).map(([label, val], idx) => (
          <div key={idx} className="bar-row-FotaStatus">
            <span className="bar-label-FotaStatus">{label}</span>
            <div className="bar-track-FotaStatus">
              <div
                className="bar-fill-FotaStatus"
                style={{
                    width: `${totalDevices > 0 ? (val / totalDevices) * 100 : 0}%`,
                    backgroundColor: "#4299E1" // Standard blue for progress
                }}
              ></div>
            </div>
            <span className="bar-stats-FotaStatus">{val}/{totalDevices}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// --- Main Page ---
const FirmwareDeploymentStatus = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Poll Backend every 2 seconds
  const { data, isLoading, error } = useQuery({
    queryKey: ['fotaStatus', id],
    queryFn: () => fetchWithAuth(`/fota/${id}/deployment-status`),
    refetchInterval: 2000,
  });

  // --- LOGIC: Calculate Active Step ---
  const activeSteps = useMemo(() => {
    if (!data || !data.steps) return [];

    const { steps, overview } = data;
    const isTotallyComplete = (overview.success_device_count + overview.fail_device_count) >= overview.device_count;

    let foundActive = false;

    return steps.map(step => {
        // 1. If step is completed, it stays completed
        if (step.status === 'completed') return step;

        // 2. Find the FIRST pending step and make it "inProgress"
        // Only do this if the whole deployment isn't already finished
        if (!foundActive && step.status === 'pending' && !isTotallyComplete) {
            foundActive = true;
            return { ...step, status: 'inProgress' };
        }

        // 3. Rest remain pending
        return step;
    });
  }, [data]);

  if (isLoading) return <div className="loading-screen">Loading Status...</div>;
  if (error) return <div className="error-screen">Error: {error.message}</div>;

  const { overview, phases } = data;

  return (
    <div className="deployment-status-app-container-FotaStatus">
      <header className="deployment-status-header-FotaStatus">
        <h1 className="deployment-status-header-title-FotaStatus">Deployment Status</h1>
        <div className="deployment-status-nav-container-FotaStatus">
          <button onClick={() => navigate(-1)} className="back-deployment-btn-FotaStatus">
            <ArrowLeft size={16} /> Back
          </button>
        </div>
      </header>

      <section className="deployment-status-section-FotaStatus">
        <h2 className="deployment-status-section-title-FotaStatus">Overview</h2>

        {/* Pass the calculated activeSteps instead of raw data.steps */}
        <ProgressBar steps={activeSteps} />

        <div className="overview-details-grid-FotaStatus">
          <div><p className="overview-detail-label-FotaStatus">Firmware Name</p><p>{overview.firmware_name}</p></div>
          <div><p className="overview-detail-label-FotaStatus">Device Group</p><p>{overview.device_group}</p></div>
          <div><p className="overview-detail-label-FotaStatus">Version</p><p>{overview.version}</p></div>
          <div><p className="overview-detail-label-FotaStatus">Created By</p><p>{overview.created_by}</p></div>
          <div><p className="overview-detail-label-FotaStatus">Start Time</p><p>{new Date(overview.start_time).toLocaleString()}</p></div>
          <div><p className="overview-detail-label-FotaStatus">Device Count</p><p>{overview.device_count}</p></div>
          <div><p className="overview-detail-label-FotaStatus">Success</p><p style={{color:"green"}}>{overview.success_device_count}</p></div>
          <div><p className="overview-detail-label-FotaStatus">Failed</p><p style={{color:"red"}}>{overview.fail_device_count}</p></div>
        </div>
      </section>

      <section className="deployment-status-section-FotaStatus">
        <h2 className="deployment-status-section-title-FotaStatus">Phases Completed</h2>
        <PhasesCompleted
            pieData={phases.pie}
            barData={phases.bars}
            totalDevices={overview.device_count}
        />
      </section>
    </div>
  );
};

export default FirmwareDeploymentStatus;